import React, { useState, useCallback } from 'react';
import { Upload, Loader2, Check, Cloud } from 'lucide-react';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useCanvasStore } from '@/stores/canvasStore';
import { toast } from 'sonner';
import { compressImage, formatBytes } from '@/lib/imageCompression';
import { generateVideoThumbnail, getVideoMetadata, VIDEO_TYPES, calculateVideoDisplaySize } from '@/lib/videoProcessing';
import { markdownToHtml, isMarkdownFile } from '@/lib/markdownUtils';

// Storage usage tracking functions
const checkStorageLimit = async (userId: string, fileSizeMB: number): Promise<boolean> => {
  const { data: settings } = await supabase
    .from('user_settings')
    .select('storage_used_mb, storage_limit_mb')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settings) return true; // Allow if no settings found

  const newUsage = settings.storage_used_mb + fileSizeMB;
  if (newUsage > settings.storage_limit_mb) {
    toast.error(`Storage limit reached (${settings.storage_limit_mb}MB). Delete some files to upload more.`);
    return false;
  }
  return true;
};

const updateStorageUsage = async (userId: string, fileSizeMB: number): Promise<void> => {
  const { data: settings } = await supabase
    .from('user_settings')
    .select('storage_used_mb, storage_limit_mb, storage_notified_50, storage_notified_100')
    .eq('user_id', userId)
    .maybeSingle();

  if (!settings) return;

  const newUsage = settings.storage_used_mb + fileSizeMB;
  const usagePercent = (newUsage / settings.storage_limit_mb) * 100;

  const updates: Record<string, unknown> = {
    storage_used_mb: newUsage,
  };

  if (usagePercent >= 50 && !settings.storage_notified_50) {
    toast.info(`You've used 50% of your storage (${newUsage.toFixed(1)}MB / ${settings.storage_limit_mb}MB)`);
    updates.storage_notified_50 = true;
  }

  if (usagePercent >= 90 && !settings.storage_notified_100) {
    toast.warning(`You're almost out of storage (${usagePercent.toFixed(0)}% used)`);
    updates.storage_notified_100 = true;
  }

  await supabase
    .from('user_settings')
    .update(updates)
    .eq('user_id', userId);
};

interface DropZoneProps {
  children: React.ReactNode;
  mode: 'canvas' | 'wall';
  onFilesUploaded?: (items: Array<{ type: 'image' | 'file' | 'video'; url: string; filename: string; mimeType: string; size: number; storagePath: string }>) => void;
  getDropPosition?: (e: React.DragEvent) => { x: number; y: number };
}

const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];

const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
};

const calculateDisplaySize = (
  naturalWidth: number, 
  naturalHeight: number,
  maxWidth: number = 400,
  maxHeight: number = 400
) => {
  const aspectRatio = naturalWidth / naturalHeight;
  
  let displayWidth = naturalWidth;
  let displayHeight = naturalHeight;
  
  // Scale down if too wide
  if (displayWidth > maxWidth) {
    displayWidth = maxWidth;
    displayHeight = displayWidth / aspectRatio;
  }
  
  // Scale down if still too tall
  if (displayHeight > maxHeight) {
    displayHeight = maxHeight;
    displayWidth = displayHeight * aspectRatio;
  }
  
  return { 
    width: Math.round(displayWidth), 
    height: Math.round(displayHeight),
    aspectRatio 
  };
};

// AI image analysis function
const analyzeImageWithAI = async (imageUrl: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('analyze-image', {
      body: { imageUrl }
    });

    if (error) {
      console.error('Image analysis error:', error);
      return null;
    }

    return data?.analysis || null;
  } catch (e) {
    console.error('Failed to analyze image:', e);
    return null;
  }
};

interface UploadState {
  total: number;
  completed: number;
  current: string;
  progress: number;
  stage: 'compressing' | 'uploading' | 'analyzing' | 'complete';
  stageText?: string;
}

export function DropZone({ children, mode, onFilesUploaded, getDropPosition }: DropZoneProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { createItem, currentBoardId, boards } = useCanvasStore();

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const uploadFile = async (file: File, onProgress?: (progress: number) => void): Promise<{ url: string; filename: string; mimeType: string; size: number; storagePath: string } | null> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('You must be logged in to upload files.');
      return null;
    }

    let fileToUpload = file;
    const isImage = IMAGE_TYPES.includes(file.type);
    
    // Compress images first to get actual file size
    if (isImage) {
      onProgress?.(10);
      const result = await compressImage(file);
      fileToUpload = result.file;
      // Compression info is shown via uploadState, not toast
      onProgress?.(20);
    }

    // Check storage limit before uploading
    const fileSizeMB = fileToUpload.size / (1024 * 1024);
    const canUpload = await checkStorageLimit(user.id, fileSizeMB);
    if (!canUpload) {
      return null;
    }

    onProgress?.(30);

    const fileExt = file.name.split('.').pop();
    // User-scoped file path for RLS compliance
    const storagePath = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

    onProgress?.(50);

    const { error: uploadError } = await supabase.storage
      .from('uploads')
      .upload(storagePath, fileToUpload);

    if (uploadError) {
      console.error('Upload error:', uploadError);
      toast.error(`Failed to upload ${file.name}. Please try again.`);
      return null;
    }

    onProgress?.(80);

    // Update storage usage after successful upload
    await updateStorageUsage(user.id, fileSizeMB);

    onProgress?.(90);

    // Use signed URL instead of public URL for private bucket
    const { data: signedUrlData, error: urlError } = await supabase.storage
      .from('uploads')
      .createSignedUrl(storagePath, 31536000); // 1 year expiry

    if (urlError || !signedUrlData) {
      console.error('Failed to get signed URL:', urlError);
      toast.error(`Failed to get URL for ${file.name}.`);
      return null;
    }

    onProgress?.(100);

    return {
      url: signedUrlData.signedUrl,
      filename: file.name,
      mimeType: file.type,
      size: fileToUpload.size,
      storagePath,
    };
  };

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    // Get target board
    let targetBoardId = currentBoardId;
    if (mode === 'wall' && !targetBoardId) {
      const homeBoard = boards.find(b => b.parent_id === null);
      targetBoardId = homeBoard?.id || null;
    }

    if (!targetBoardId) {
      toast.error('No board selected. Please navigate to a board first.');
      return;
    }

    setUploadState({ total: files.length, completed: 0, current: files[0].name, progress: 0, stage: 'uploading' });

    const dropPosition = getDropPosition ? getDropPosition(e) : { x: 100, y: 100 };
    const uploadedItems: Array<{ type: 'image' | 'file' | 'video'; url: string; filename: string; mimeType: string; size: number; storagePath: string }> = [];
    let offsetX = 0;
    let offsetY = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadState(prev => prev ? { ...prev, current: file.name, completed: i, progress: 0, stage: 'uploading' } : prev);
      
      // Handle markdown files - convert to text card or document based on length
      if (isMarkdownFile(file.name)) {
        try {
          const markdownText = await file.text();
          const htmlContent = markdownToHtml(markdownText);
          const filename = file.name.replace(/\.(md|markdown|mdown|mkd|mkdn)$/i, '');
          
          // Use document for longer content (>500 chars), text card for shorter
          const isLongContent = markdownText.length > 500;
          
          await createItem({
            board_id: targetBoardId,
            item_type: isLongContent ? 'document' : 'text_card',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width: isLongContent ? 280 : 320,
            height: isLongContent ? 120 : 400,
            content: { 
              body: htmlContent,
              title: filename,
            },
          });
          
          offsetX += 20;
          offsetY += 20;
          toast.success(`${isLongContent ? 'Document' : 'Text card'} "${filename}" added to canvas`);
        } catch (err) {
          console.error('Failed to read markdown file:', err);
          toast.error(`Failed to process ${file.name}`);
        }
        continue;
      }
      
      const result = await uploadFile(file, (progress) => {
        setUploadState(prev => prev ? { ...prev, progress } : prev);
      });
      
      if (result) {
        const isImage = IMAGE_TYPES.includes(file.type);
        const isVideo = VIDEO_TYPES.includes(file.type);
        
        uploadedItems.push({ 
          ...result, 
          type: isImage ? 'image' : isVideo ? 'video' : 'file' 
        });

        let width = 280;
        let height = 100;
        let itemContent: Record<string, unknown> = {};

        if (isImage) {
          try {
            const dims = await getImageDimensions(file);
            const size = calculateDisplaySize(dims.width, dims.height);
            width = size.width;
            height = size.height;
            itemContent = { 
              naturalWidth: dims.width, 
              naturalHeight: dims.height, 
              aspectRatio: size.aspectRatio 
            };
          } catch (e) {
            console.warn('Could not get image dimensions, using defaults');
            width = 400;
            height = 300;
          }

          // Analyze image with AI (non-blocking - start analysis)
          setUploadState(prev => prev ? { ...prev, stage: 'analyzing', stageText: 'Analyzing with AI' } : prev);
          
          // Start AI analysis - it will be added to the item asynchronously
          const analysisPromise = analyzeImageWithAI(result.url);
          
          // Create item first so user sees it immediately
          const createdItem = await createItem({
            board_id: targetBoardId,
            item_type: 'image',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width,
            height,
            content: { 
              url: result.url, 
              alt: result.filename, 
              storagePath: result.storagePath,
              mimeType: file.type,
              ...itemContent 
            },
          });

          // Wait for analysis and update item
          const analysis = await analysisPromise;
          if (analysis && createdItem) {
            // Update item with AI analysis for search
            const searchText = `${result.filename} ${analysis}`.toLowerCase();
            await supabase
              .from('items')
              .update({ 
                content: { 
                  url: result.url, 
                  alt: result.filename, 
                  storagePath: result.storagePath,
                  mimeType: file.type,
                  ...itemContent,
                  analysis,
                  searchText
                } 
              })
              .eq('id', createdItem.id);
          }
        } else if (isVideo) {
          // Handle video upload with thumbnail generation
          setUploadState(prev => prev ? { ...prev, stage: 'uploading', stageText: 'Generating thumbnail' } : prev);
          
          let thumbnailUrl: string | null = null;
          let thumbnailStoragePath: string | null = null;
          let duration = 0;
          
          try {
            // Get video metadata
            const metadata = await getVideoMetadata(file);
            duration = metadata.duration;
            
            // Calculate display size for video
            const displaySize = calculateVideoDisplaySize(metadata.width, metadata.height);
            width = displaySize.width;
            height = displaySize.height;
            itemContent = {
              naturalWidth: metadata.width,
              naturalHeight: metadata.height,
              aspectRatio: displaySize.aspectRatio,
            };
            
            // Generate thumbnail
            const thumbnailBlob = await generateVideoThumbnail(file);
            const thumbnailFile = new File([thumbnailBlob], `${file.name}-thumbnail.jpg`, { type: 'image/jpeg' });
            
            // Upload thumbnail
            const { data: { user } } = await supabase.auth.getUser();
            if (user) {
              const thumbnailPath = `${user.id}/${Date.now()}-thumbnail.jpg`;
              const { error: thumbUploadError } = await supabase.storage
                .from('uploads')
                .upload(thumbnailPath, thumbnailFile);
              
              if (!thumbUploadError) {
                thumbnailStoragePath = thumbnailPath;
                const { data: signedUrl } = await supabase.storage
                  .from('uploads')
                  .createSignedUrl(thumbnailPath, 31536000);
                
                if (signedUrl) {
                  thumbnailUrl = signedUrl.signedUrl;
                }
              }
            }
          } catch (e) {
            console.warn('Could not generate video thumbnail:', e);
            // Use defaults
            width = 320;
            height = 180;
          }
          
          // Analyze video with AI
          setUploadState(prev => prev ? { ...prev, stage: 'analyzing', stageText: 'Analyzing video' } : prev);
          
          // Use the analyze-video function for video analysis
          const analysisPromise = supabase.functions.invoke('analyze-video', {
            body: { videoUrl: result.url, thumbnailUrl }
          }).then(({ data }) => data?.analysis || null).catch(() => null);
          
          // Create video item first
          const createdItem = await createItem({
            board_id: targetBoardId,
            item_type: 'video',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width,
            height,
            content: { 
              videoUrl: result.url, 
              thumbnailUrl,
              filename: result.filename,
              duration,
              mimeType: result.mimeType,
              size: result.size,
              storagePath: result.storagePath,
              thumbnailStoragePath,
              ...itemContent 
            },
          });
          
          // Update with AI analysis when ready
          const analysis = await analysisPromise;
          if (analysis && createdItem) {
            const searchText = `${result.filename} ${analysis}`.toLowerCase();
            await supabase
              .from('items')
              .update({
                content: {
                  videoUrl: result.url,
                  thumbnailUrl,
                  filename: result.filename,
                  duration,
                  mimeType: result.mimeType,
                  size: result.size,
                  storagePath: result.storagePath,
                  thumbnailStoragePath,
                  aiDescription: analysis,
                  searchText,
                  ...itemContent
                }
              })
              .eq('id', createdItem.id);
          }
        } else {
          // Non-image, non-video file
          // PDFs get a larger card for better preview
          const isPdf = result.mimeType === 'application/pdf';
          const fileWidth = isPdf ? 280 : 280;
          const fileHeight = isPdf ? 340 : 100;

          await createItem({
            board_id: targetBoardId,
            item_type: 'file',
            position_x: dropPosition.x + offsetX,
            position_y: dropPosition.y + offsetY,
            width: fileWidth,
            height: fileHeight,
            content: { url: result.url, filename: result.filename, mimeType: result.mimeType, size: result.size, storagePath: result.storagePath },
          });
        }

        offsetX += 20;
        offsetY += 20;
      }
    }

    // Show success animation
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setUploadState(null);
    }, 2000);

    if (uploadedItems.length > 0) {
      // Success shown in notification bar, no toast needed
      onFilesUploaded?.(uploadedItems);
    }
  }, [currentBoardId, boards, mode, createItem, getDropPosition, onFilesUploaded]);

  const isUploading = uploadState !== null && !showSuccess;
  const overallProgress = uploadState 
    ? ((uploadState.completed + (uploadState.progress / 100)) / uploadState.total) * 100
    : 0;

  return (
    <div
      className="relative w-full h-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}

      {/* Drop overlay - only show when actively dragging */}
      {isDragOver && !isUploading && (
        <div
          className={cn(
            "absolute inset-0 z-[100] flex items-center justify-center",
            "bg-primary/10 border-2 border-dashed border-primary rounded-lg",
            "transition-all duration-200 ease-out",
            "animate-in fade-in-0"
          )}
        >
          <div className="flex flex-col items-center gap-4 text-primary">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Upload className="h-8 w-8" />
            </div>
            <p className="text-lg font-medium">
              {mode === 'canvas' ? 'Drop to add to canvas' : 'Drop files here'}
            </p>
            <p className="text-sm text-muted-foreground">
              Images will be compressed automatically
            </p>
          </div>
        </div>
      )}

      {/* Beautiful corner notification for upload progress */}
      {(isUploading || showSuccess) && (
        <div 
          className={cn(
            "fixed bottom-6 right-6 z-[200]",
            "flex items-center gap-3 px-5 py-4",
            "bg-background/95 backdrop-blur-xl",
            "rounded-2xl shadow-2xl border border-border/50",
            "animate-in slide-in-from-bottom-4 fade-in duration-300"
          )}
        >
          {showSuccess ? (
            <>
              {/* Success checkmark with gradient */}
              <div className="relative w-9 h-9 flex-shrink-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                  <Check className="h-5 w-5 text-white" />
                </div>
              </div>
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-foreground">
                  Saved to Mindfull
                </span>
                <span className="text-xs text-muted-foreground">
                  {uploadState?.total || 1} item{(uploadState?.total || 1) > 1 ? 's' : ''} added
                </span>
              </div>
            </>
          ) : (
            <>
              {/* Gradient spinning ring - orange/amber like mymind */}
              <div className="relative w-9 h-9 flex-shrink-0">
                <svg 
                  className="w-9 h-9 animate-spin" 
                  viewBox="0 0 36 36"
                  style={{ animationDuration: '1.2s' }}
                >
                  <defs>
                    <linearGradient id="uploadGradientRing" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f97316" />
                      <stop offset="50%" stopColor="#f59e0b" />
                      <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                  </defs>
                  <circle
                    cx="18"
                    cy="18"
                    r="15"
                    fill="none"
                    stroke="url(#uploadGradientRing)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeDasharray="65 30"
                  />
                </svg>
                
                {/* Inner glow */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-amber-400/30 to-orange-500/30" />
                </div>
              </div>

              {/* Text content */}
              <div className="flex flex-col min-w-0">
                <span className="text-sm font-semibold text-foreground">
                  {uploadState?.stage === 'analyzing' 
                    ? 'Analyzing with Partnr'
                    : uploadState?.stage === 'compressing'
                    ? 'Compressing'
                    : 'Saving to Mindfull'
                  }
                </span>
                <span className="text-xs text-muted-foreground">
                  {uploadState?.stageText || (
                    uploadState?.completed !== undefined && uploadState?.total !== undefined
                      ? `${uploadState.completed + 1} of ${uploadState.total} items`
                      : uploadState?.current
                  )}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
